import React from "react";
import { Container, cssVariables, Icon } from "../../components";
import { OverlayTrigger } from "react-bootstrap";
import Popover from "react-bootstrap/Popover";
import { formatCurrency } from "../../lib/currency/currency";
import { useI18n } from "../../lib/i18n/i18n_provider";
import { getSystemLocale } from "../../lib/locale/locale";
import { FeeLine, PromotionLine, TaxLine } from "../../offering/api";
import { Spacer } from "../../offering/spacer";
import "./membership_price_info.css";
import { AdditionalFeesItem } from "./additional_fees_item";

interface MembershipPriceInfoProps {
  firstMonthAdjustment?: number;
  currency: string | null;
  monthlyPrice: number;
  promotion: Pick<PromotionLine, "amount" | "code"> | null;
  taxLine: Pick<TaxLine, "description" | "amount"> | null;
  paymentCycles: number;
  feeLines?: FeeLine[];
  feeLinesDisplay?: "modal" | "list";
}

export function MembershipPriceInfo(props: MembershipPriceInfoProps) {
  const {
    monthlyPrice,
    firstMonthAdjustment,
    promotion,
    currency,
    taxLine,
    paymentCycles,
    feeLines,
    feeLinesDisplay = "modal",
  } = props;
  const { fbt } = useI18n();

  return (
    <div>
      <div className="info-line">
        <span>
          {paymentCycles > 0
            ? fbt(
                "First " +
                  fbt.param("cycles", paymentCycles + 1) +
                  " months payment"
              )
            : fbt("Monthly payment")}
        </span>
        <span>
          {formatCurrency(
            monthlyPrice * (paymentCycles + 1),
            currency,
            getSystemLocale()
          )}
        </span>
      </div>
      {!!firstMonthAdjustment && (
        <>
          <Spacer size={21} />
          <div className="info-line">
            <div className="popover-info-wrapper">
              <span className="info-green-line">
                {fbt("First month adjustment")}
              </span>
              <Spacer direction="horizontal" size={4} />
              <OverlayTrigger
                placement="auto"
                overlay={
                  <Popover
                    className="membership-price-info-popover-wrapper"
                    style={{
                      backgroundColor: "white",
                      borderColor: cssVariables.borderColorDefault,
                      borderWidth: 1,
                      borderRadius: cssVariables.radius,
                      boxShadow: cssVariables.shadow,
                      padding: "16px",
                      minWidth: "264px",
                    }}
                  >
                    <Container>
                      <span className="membership-payment-info-popover">
                        {fbt(
                          "Payments are calculated monthly. Your first payment covers the remaining days for the current month. Afterwards, you'll be billed on or about the first of the month."
                        )}
                      </span>
                    </Container>
                  </Popover>
                }
              >
                <span>
                  <Icon name="info" size={16} color="rgba(3, 130, 49, 1)" />
                </span>
              </OverlayTrigger>
            </div>
            <span
              data-testid="membership-applied-adjustment-amount"
              className="adjusted-price"
            >
              {formatCurrency(
                -Math.abs(firstMonthAdjustment),
                currency,
                getSystemLocale()
              )}
            </span>
          </div>
        </>
      )}

      {promotion && (
        <>
          <Spacer size={21} />
          <div className="info-line">
            <span
              data-testid="membership-applied-promo-code"
              className="info-green"
            >
              {promotion.code}
            </span>
            <span
              data-testid="membership-applied-promo-amount"
              className="info-green"
            >
              {formatCurrency(
                -Math.abs(promotion.amount),
                currency,
                getSystemLocale()
              )}
            </span>
          </div>
        </>
      )}

      {!!feeLines?.length && (
        <>
          <Spacer size={21} />
          <AdditionalFeesItem
            currency={currency}
            feeLines={feeLines}
            display={feeLinesDisplay}
          />
        </>
      )}

      {taxLine && (
        <>
          <Spacer size={21} />
          <div className="info-line">
            <span>{taxLine.description}</span>
            <span>
              {formatCurrency(taxLine.amount, currency, getSystemLocale())}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
